// Key normalization helpers for object-variable lookup/expansion.
// Example: "fontSize" <-> "font-size" fallback resolution.
import type { LuisObjectVariables } from '../../types'

// Resolve object key using exact and normalized key candidates.
export function getObjectVariableValue(
  objectVariables: LuisObjectVariables,
  objectName: string,
  key: string,
): string | undefined {
  const objectValue = objectVariables[objectName]
  // Return undefined when object variable name does not exist.
  if (!objectValue) {
    return undefined
  } else {
    // Continue key candidate fallback search.
  }

  const candidates = [
    key,
    toCssPropertyName(key),
    toCamelCase(key),
    key.toLowerCase(),
    toCssPropertyName(key).toLowerCase(),
  ]

  // Try direct and normalized key candidates first.
  for (const candidate of candidates) {
    if (Object.prototype.hasOwnProperty.call(objectValue, candidate)) {
      return objectValue[candidate]
    } else {
      // Continue with next fallback candidate.
    }
  }

  const lower = key.toLowerCase()
  // Fallback to case-insensitive lookup over stored object keys.
  for (const [entryKey, entryValue] of Object.entries(objectValue)) {
    if (entryKey.toLowerCase() === lower || toCssPropertyName(entryKey).toLowerCase() === lower) {
      return entryValue
    } else {
      // Continue scanning object entries.
    }
  }

  // Return undefined when no key candidate matches.
  return undefined
}

// Convert camelCase object keys into CSS property style when needed.
export function toCssPropertyName(key: string): string {
  // Keep already-custom-property keys untouched.
  if (key.startsWith('--')) {
    return key
  } else {
    // Return kebab-case conversion for non-custom keys.
    return key.replace(/[A-Z]/g, (char) => `-${char.toLowerCase()}`)
  }
}

// Convert kebab-case key names into camelCase lookup candidates.
function toCamelCase(key: string): string {
  // Return camelCase conversion for kebab-case keys.
  return key.replace(/-([a-z])/g, (_, c: string) => c.toUpperCase())
}
